import React from "react";
import { Link } from "react-router-dom";
import useSeo from "../hooks/useSeo";
import ResultCard from "./ResultCard";
import { api, dmyToSlash } from "../services/api";
import { ChevronRight, Home } from "lucide-react";

const ORIGIN = "https://groundsundayteer.com";

// India/IST helpers
function istISO() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}
function istLongDate() {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date());
}

// SEO landing page: real, indexable content + embedded live data (result/common/previous)
// so visitors see the answer immediately and Google can index the page.
export default function SeoLanding({ title, description, canonicalPath, h1, breadcrumb, ctaLabel, ctaTo, paragraphs = [], related = [] }) {
  useSeo({ title, description, canonical: `${ORIGIN}${canonicalPath}` });

  const [embedded, setEmbedded] = React.useState(null);

  React.useEffect(() => {
    if (ctaTo === "/") {
      // Result pages: show today's result (or latest published as fallback)
      const iso = istISO();
      const mapResult = (r) => ({
        date: dmyToSlash(r.date),
        city: r.city,
        frTime: r.frTime,
        srTime: r.srTime,
        fr: r.fr,
        sr: r.sr,
      });
      api
        .resultByDate(iso)
        .then(async (r) => {
          if (r && r.fr && r.sr) {
            setEmbedded({ type: "result", data: mapResult(r) });
            return;
          }
          try {
            const latest = await api.latestResult();
            if (latest && latest.fr && latest.sr) {
              setEmbedded({ type: "result", data: mapResult(latest) });
            }
          } catch {}
        })
        .catch(() => {});
    } else if (ctaTo === "/common-number") {
      api
        .commonNumber()
        .then((d) => {
          if (d) setEmbedded({ type: "common", data: d });
        })
        .catch(() => {});
    } else if (ctaTo === "/previous-result") {
      api
        .results({ limit: 5 })
        .then((r) => {
          if (r?.items?.length) setEmbedded({ type: "previous", data: r.items });
        })
        .catch(() => {});
    } else if (ctaTo === "/club-chart") {
      // Club chart page: just show today's result card as preview
      const iso = istISO();
      api
        .resultByDate(iso)
        .then((r) => {
          if (r && r.fr && r.sr) {
            setEmbedded({ type: "result", data: { date: dmyToSlash(r.date), city: r.city, frTime: r.frTime, srTime: r.srTime, fr: r.fr, sr: r.sr } });
          }
        })
        .catch(() => {});
    }
  }, [ctaTo]);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${ORIGIN}/` },
      { "@type": "ListItem", position: 2, name: breadcrumb, item: `${ORIGIN}${canonicalPath}` },
    ],
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="seo-landing-page">
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <nav className="flex items-center gap-1.5 text-sm text-gray-500 mb-6" aria-label="Breadcrumb">
        <Link to="/" className="inline-flex items-center gap-1 hover:text-[#e8491d] transition-colors">
          <Home className="w-4 h-4" /> Home
        </Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-[#1f2733] font-semibold">{breadcrumb}</span>
      </nav>

      <h1 className="text-2xl sm:text-3xl font-black text-[#1f2733] mb-4">{h1}</h1>

      {/* Embedded live content — visitors see the answer immediately */}
      {embedded?.type === "result" && (
        <div className="mb-6" data-testid="seo-embedded-result">
          <p className="text-center text-sm text-gray-500 mb-2">{istLongDate()} • F/R 4:30 PM · S/R 5:30 PM</p>
          <ResultCard result={embedded.data} />
        </div>
      )}

      {embedded?.type === "common" && (
        <div className="mb-6 border-2 border-gray-300 rounded-xl overflow-hidden bg-white" data-testid="seo-embedded-common">
          <div className="text-center font-black text-lg py-2 bg-[#eaeaea] border-b-2 border-gray-300">
            Sunday Ground Teer Common Number
          </div>
          <div className="text-center text-sm text-gray-500 py-1 border-b border-gray-200">{istLongDate()}</div>
          <div className="grid grid-cols-3 gap-0">
            <div className="text-center py-4 border-r border-gray-300">
              <div className="text-xs font-bold text-gray-500 mb-1">DIRECT</div>
              <div className="text-2xl font-black text-[#1f2733]">{embedded.data.direct || "—"}</div>
            </div>
            <div className="text-center py-4 border-r border-gray-300">
              <div className="text-xs font-bold text-gray-500 mb-1">HOUSE</div>
              <div className="text-2xl font-black text-[#1f2733]">{embedded.data.house || "—"}</div>
            </div>
            <div className="text-center py-4">
              <div className="text-xs font-bold text-gray-500 mb-1">ENDING</div>
              <div className="text-2xl font-black text-[#1f2733]">{embedded.data.ending || "—"}</div>
            </div>
          </div>
        </div>
      )}

      {embedded?.type === "previous" && (
        <div className="mb-6 border-2 border-gray-300 rounded-xl overflow-hidden bg-white" data-testid="seo-embedded-previous">
          <div className="text-center font-black text-lg py-2 bg-[#eaeaea] border-b-2 border-gray-300">
            Latest Previous Results
          </div>
          {embedded.data.map((r) => (
            <div key={r._id || r.date} className="grid grid-cols-4 border-b border-gray-200 text-sm">
              <div className="text-center py-2 font-semibold text-gray-600 border-r border-gray-200">{dmyToSlash(r.date)}</div>
              <div className="text-center py-2 text-gray-500 border-r border-gray-200">F/R</div>
              <div className="text-center py-2 font-black text-[#1f2733] border-r border-gray-200">{r.fr}</div>
              <div className="text-center py-2 font-black text-[#1f2733]">{r.sr}</div>
            </div>
          ))}
        </div>
      )}

      {paragraphs.map((p, i) => (
        <p key={i} className="text-gray-600 leading-relaxed mb-4">
          {p}
        </p>
      ))}

      <div className="mt-6">
        <Link
          to={ctaTo}
          data-testid="seo-landing-cta"
          className="inline-flex items-center gap-1.5 px-6 py-3 rounded-xl bg-[#e8491d] text-white font-bold hover:bg-[#c53a12] transition-colors"
        >
          {ctaLabel} <ChevronRight className="w-5 h-5" />
        </Link>
      </div>

      {related.length > 0 && (
        <div className="mt-10 pt-6 border-t border-gray-200">
          <h2 className="text-base md:text-lg font-bold text-[#1f2733] mb-3">Related Pages</h2>
          <ul className="space-y-2">
            {related.map((r) => (
              <li key={r.to}>
                <Link to={r.to} className="text-[#e8491d] font-semibold hover:underline inline-flex items-center gap-1">
                  <ChevronRight className="w-4 h-4" /> {r.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
